/**
 * ImageRevealParallax — data-mask-image 래퍼 기반 클립 리빌 + 패럴랙스
 *
 * 마크업:
 *   <div data-mask-image data-image-reveal-duration="0.9" data-image-reveal-y="12">
 *     <div class="data-parallax-image">
 *       <figure class="..."><img></figure>
 *     </div>
 *   </div>
 *
 * - [data-mask-image]: 마스크 역할, clipPath 리빌 (inset 0 0 100% 0 → 0 0 0 0)
 * - .data-parallax-image: 패럴랙스 대상 (scale 1.24, yPercent scrub)
 * - .main-glass-hero 내부는 제외
 * - prefers-reduced-motion, 중복 init 방지, 이미지 로드 후 refresh
 */
const ATTR_MASK = "data-mask-image";
const ATTR_DURATION = "data-image-reveal-duration";
const ATTR_PARALLAX_Y = "data-image-reveal-y";
const DATA_INITED = "data-mask-image-inited";
const DATA_REVEALED = "data-mask-image-revealed";
const PARALLAX_SCALE = 1.24; // y ±10 대비 여유
const ROOT_MARGIN_REVEAL = "0px 0px 200px 0px"; // 빠른 스크롤 시 뷰포트 진입 전에 트리거
const ROOT_MARGIN_BOTTOM_PX = 200; // ROOT_MARGIN_REVEAL 하단 200px와 동일 (초기/지연 체크용)

/** 스크롤 지나쳤을 때 / 새로고침 시 이미 뷰에 있는 요소 리빌 실행 여부. 기본 true */
export const ImageRevealConfig = { REVEAL_WHEN_ALREADY_IN_VIEW: true };

/** 빠른 스크롤 시 observer가 놓친 요소 보정용: 요소별 playClip 콜백 */
const revealCallbacks = new WeakMap();

function shouldSkip(el) {
    return el.closest(".main-glass-hero") != null;
}

function prefersReducedMotion() {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

function parseDuration(el) {
    const raw = el.getAttribute(ATTR_DURATION);
    if (raw == null || raw === "") return 0.9;
    const n = parseFloat(raw, 10);
    return Number.isFinite(n) && n > 0 ? n : 0.9;
}

function parseParallaxY(el) {
    const raw = el.getAttribute(ATTR_PARALLAX_Y);
    if (raw == null || raw === "") return 12;
    const n = parseFloat(raw, 10);
    return Number.isFinite(n) ? n : 12;
}

function initMaskImage(maskEl) {
    if (maskEl.getAttribute(DATA_INITED) === "true") return;
    const parallaxEl = maskEl.querySelector(".data-parallax-image");
    const maskOnly = !parallaxEl; // .data-parallax-image 없으면 마스크 리빌만 (예: Contact 블록 전체)

    const figure = parallaxEl ? parallaxEl.querySelector("figure") : maskEl.querySelector("figure");
    const img = figure ? figure.querySelector("img") : maskEl.querySelector("img");
    const video = figure ? figure.querySelector("video") : maskEl.querySelector("video");
    maskEl.setAttribute(DATA_INITED, "true");

    const duration = parseDuration(maskEl);
    const parallaxY = parseParallaxY(maskEl);
    const reduceMotion = prefersReducedMotion();

    // 1) 마스크: clipPath 초기 상태 (리빌 전 숨김)
    gsap.set(maskEl, {
        overflow: "hidden",
        clipPath: "inset(0 0 100% 0)",
        willChange: reduceMotion ? "auto" : "clip-path",
    });

    if (!maskOnly) {
        gsap.set(parallaxEl, {
            transformOrigin: "center center",
            scale: PARALLAX_SCALE,
            yPercent: -parallaxY,
            force3D: true,
        });
    }

    if (!reduceMotion) {
        const playClip = () => {
            if (maskEl.getAttribute(DATA_REVEALED) === "true") return;
            maskEl.setAttribute(DATA_REVEALED, "true");
            gsap.to(maskEl, {
                clipPath: "inset(0 0 0% 0)",
                duration,
                ease: "power3.inOut",
                overwrite: true,
                onComplete: () => {
                    gsap.set(maskEl, { willChange: "auto" });
                },
            });
        };

        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (!entry.isIntersecting) return;
                    playClip();
                    observer.unobserve(entry.target);
                });
            },
            { threshold: 0, rootMargin: ROOT_MARGIN_REVEAL }
        );
        observer.observe(maskEl);

        revealCallbacks.set(maskEl, playClip);

        // observer와 동일한 영역 기준: rootMargin 200px 하단까지 진입했으면 리빌
        const rect = maskEl.getBoundingClientRect();
        const inViewForReveal = rect.top < window.innerHeight + ROOT_MARGIN_BOTTOM_PX && rect.bottom > 0;
        if (ImageRevealConfig.REVEAL_WHEN_ALREADY_IN_VIEW && inViewForReveal) {
            requestAnimationFrame(() => playClip());
        }

        if (!maskOnly) {
            gsap.fromTo(
                parallaxEl,
                { yPercent: -parallaxY },
                {
                    yPercent: parallaxY,
                    ease: "none",
                    scrollTrigger: {
                        trigger: maskEl,
                        start: "top bottom",
                        end: "bottom top",
                        scrub: 1,
                    },
                }
            );
        }
    } else {
        gsap.set(maskEl, { clipPath: "inset(0 0 0% 0)", willChange: "auto" });
    }

    if (img) {
        if (img.complete) {
            ScrollTrigger.refresh();
        } else {
            img.addEventListener("load", () => ScrollTrigger.refresh());
        }
    }
    if (video) {
        const onVideoReady = () => ScrollTrigger.refresh();
        if (video.readyState >= 2) {
            onVideoReady();
        } else {
            video.addEventListener("loadeddata", onVideoReady, { once: true });
        }
    }
}

/** observer와 동일한 영역: rootMargin 200px 하단까지 진입했으면 인뷰로 간주 */
function isInViewForReveal(el) {
    const r = el.getBoundingClientRect();
    return r.top < window.innerHeight + ROOT_MARGIN_BOTTOM_PX && r.bottom > 0;
}

function runRevealFallback() {
    if (!ImageRevealConfig.REVEAL_WHEN_ALREADY_IN_VIEW) return;
    document.querySelectorAll(`[${ATTR_MASK}][${DATA_INITED}="true"]:not([${DATA_REVEALED}="true"])`).forEach((el) => {
        if (isInViewForReveal(el)) {
            const cb = revealCallbacks.get(el);
            if (cb) cb();
        }
    });
}

export function initImageRevealParallax() {
    if (typeof gsap === "undefined") return;
    if (typeof ScrollTrigger !== "undefined" && gsap.registerPlugin) {
        gsap.registerPlugin(ScrollTrigger);
    }

    const candidates = document.querySelectorAll(`[${ATTR_MASK}]`);
    const elements = Array.from(candidates).filter((el) => !shouldSkip(el));
    elements.forEach((maskEl) => initMaskImage(maskEl));

    // 새로고침 시 이미 뷰에 있는 요소: 레이아웃 안정 후 한 번 더 체크 (observer와 동일 영역)
    if (ImageRevealConfig.REVEAL_WHEN_ALREADY_IN_VIEW) {
        setTimeout(runRevealFallback, 150);
    }
    // 빠른 스크롤 시 observer가 놓친 요소 보정: 400ms 후 + 스크롤 시 뷰포트 내 미노출 마스크 강제 리빌
    setTimeout(runRevealFallback, 400);
    let scrollTid = null;
    const onScroll = () => {
        if (scrollTid != null) clearTimeout(scrollTid);
        scrollTid = setTimeout(runRevealFallback, 80);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    const cleanup = () => window.removeEventListener("scroll", onScroll, { passive: true });
    setTimeout(cleanup, 4000);
}

/* ============================================
   Card / List Item Morph Reveal (data-mask-reveal)
   카드·리스트 아이템 전용: 뷰포트 진입 시 scale + opacity morph 등장 (swiper 제외)
   data-mask-reveal
   data-mask-reveal-duration (기본 0.65)
   data-mask-reveal-stagger (그리드 내 stagger, 기본 0.08)
   ============================================ */
const ATTR_CARD_MASK = "data-mask-reveal";
const ATTR_CARD_DURATION = "data-mask-reveal-duration";
const ATTR_CARD_STAGGER = "data-mask-reveal-stagger";
const DATA_CARD_INITED = "data-mask-reveal-inited";
const MORPH_SCALE_FROM = 0.88;
const MORPH_OPACITY_FROM = 0.72;
const MORPH_Y_FROM = 18;

function parseCardDuration(el) {
    const raw = el.getAttribute(ATTR_CARD_DURATION);
    if (raw == null || raw === "") return 0.65;
    const n = parseFloat(raw, 10);
    return Number.isFinite(n) && n > 0 ? n : 0.65;
}

function parseCardStagger(el) {
    const raw = el.getAttribute(ATTR_CARD_STAGGER);
    if (raw == null || raw === "") return 0.08;
    const n = parseFloat(raw, 10);
    return Number.isFinite(n) && n >= 0 ? n : 0.08;
}

function setupCardMorphRevealForElement(el, index, siblings) {
    if (el.getAttribute(DATA_CARD_INITED) === "true") return;
    if (el.closest(".main-glass-hero")) return;
    if (el.closest(".swiper-slide")) return;

    el.setAttribute(DATA_CARD_INITED, "true");
    const duration = parseCardDuration(el);
    const stagger = parseCardStagger(el);
    const reduceMotion = prefersReducedMotion();

    gsap.set(el, {
        opacity: MORPH_OPACITY_FROM,
        scale: MORPH_SCALE_FROM,
        y: MORPH_Y_FROM,
        transformOrigin: "center center",
        willChange: reduceMotion ? "auto" : "transform, opacity",
    });

    if (reduceMotion) {
        gsap.set(el, {
            opacity: 1,
            scale: 1,
            y: 0,
            willChange: "auto",
        });
        return;
    }

    const delay = siblings.length > 1 ? index * stagger : 0;
    const tl = gsap.timeline({
        scrollTrigger: {
            trigger: el,
            start: "top 88%",
            end: "top 75%",
            toggleActions: "play none none none",
        },
        defaults: { ease: "power3.out" },
    });

    tl.to(
        el,
        {
            opacity: 1,
            scale: 1,
            y: 0,
            duration,
            delay,
            overwrite: true,
            onComplete: () => gsap.set(el, { willChange: "auto" }),
        },
        0
    );
}

export function initCardMaskReveal() {
    if (typeof gsap === "undefined") return;
    if (typeof ScrollTrigger !== "undefined" && gsap.registerPlugin) {
        gsap.registerPlugin(ScrollTrigger);
    }

    const elements = document.querySelectorAll(`[${ATTR_CARD_MASK}]`);
    elements.forEach((el) => {
        if (el.closest(".swiper-slide")) return;
        const parent = el.parentElement;
        const siblings = parent ? Array.from(parent.children).filter((c) => c.matches(`[${ATTR_CARD_MASK}]`) && !c.closest(".swiper-slide")) : [el];
        const index = siblings.indexOf(el);
        setupCardMorphRevealForElement(el, index, siblings);
    });
}
